"use client";
import { useState, type JSX } from "react";

import {
  Tabs as TabsUI,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";

export type TabsItem = {
  tabHeading: string;
  content: JSX.Element;
};

export type TabsProps = {
  tabs: TabsItem[];
};

export default function Tabs({ tabs }: TabsProps) {
  const [activeTab, setActiveTab] = useState<TabsItem>(tabs[0]);
  const contentStyles = "rounded-t-none shadow-none border-x-0 border-b-0";

  const handleTabChange = (
    tabSlug: string,
    triggeredByValidAction: boolean,
  ) => {
    if (triggeredByValidAction) {
      const tab = tabs.find(
        (tab) => sanitizeTabValue(tab.tabHeading) === tabSlug,
      );
      if (tab) setActiveTab(tab);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, tabSlug: string) => {
    if (event.key === "Enter") {
      handleTabChange(tabSlug, true); // Valid action
    }
  };

  const handleClick = (tabSlug: string) => {
    handleTabChange(tabSlug, true); // Valid action
  };

  const handleSelectChange = (tabHeading: string) => {
    const tab = tabs.find((tab) => tab.tabHeading === tabHeading);
    if (tab) setActiveTab(tab);
  };

  return (
    <div className="not-prose">
      {/* TABS */}
      <div className="hidden lg:block">
        <TabsUI
          value={sanitizeTabValue(activeTab.tabHeading)}
          className="border rounded-xl w-full"
          onValueChange={(tabSlug) => handleTabChange(tabSlug, false)} // Default to invalid action
        >
          <TabsList>
            {tabs.map((tab) => {
              const sanitizedValue = sanitizeTabValue(tab.tabHeading);

              return (
                <TabsTrigger
                  key={sanitizedValue}
                  value={sanitizedValue}
                  onClick={() => handleClick(sanitizedValue)}
                  onKeyDown={(event) => handleKeyDown(event, sanitizedValue)}
                >
                  {tab.tabHeading}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {tabs.map((tab) => {
            const sanitizedValue = sanitizeTabValue(tab.tabHeading);

            return (
              <TabsContent
                key={sanitizedValue}
                value={sanitizedValue}
                className="m-0"
              >
                <Card className={contentStyles}>{tab.content}</Card>
              </TabsContent>
            );
          })}
        </TabsUI>
      </div>
      {/* SELECT */}
      <div className="block lg:hidden">
        <Select value={activeTab.tabHeading} onValueChange={handleSelectChange}>
          <SelectTrigger
            className="h-auto rounded-none bg-primary-400 text-base text-white hover:text-white no-underline p-4 border-none"
            id="selectbutton"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {tabs.map((tab) => (
              <SelectItem
                key={`select-item-${tab.tabHeading}`}
                value={tab.tabHeading}
                className="text-base"
              >
                {tab.tabHeading}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Card className={contentStyles}>{activeTab.content}</Card>
      </div>
    </div>
  );
}
